import { useOrganizationList } from "@/api/services/admin/organization";
import { Tab } from "@/components/elements/Tab";
import { START_PAGE } from "@/constants/pagination";
import { ORGANIZATION_STATUS } from "@/features/organization/constants/organizationList";
import { useGetOrganizationList } from "@/features/organization/hooks/useGetOrganizationTabs";
import { useOrganizationFilterCache } from "@/features/organization/providers/OrganizationListFilterCacheProvider";
import { usePagination } from "@/hooks/usePagination";
import { text } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";

type Props = {
  children: React.ReactNode;
};

const OrganizationLayout = ({ children }: Props) => {
  const { filterCache, setFilterCache } = useOrganizationFilterCache();
  const pagReq = usePagination();

  const hasFilter = !!(
    filterCache?.externalId ||
    filterCache?.name ||
    filterCache?.productId ||
    filterCache?.lastLoginFrom ||
    filterCache?.lastLoginTo ||
    filterCache?.createdDateFrom ||
    filterCache?.createdDateTo
  );

  const { data, isFetching } = useOrganizationList({
    page: filterCache.page,
    status: filterCache.status || undefined,
    search: hasFilter ? undefined : filterCache.search || undefined,
    createdDateFrom: filterCache?.createdDateFrom ?? undefined,
    createdDateTo: filterCache?.createdDateTo ?? undefined,
    externalId: filterCache?.externalId ?? undefined,
    lastLoginFrom: filterCache?.lastLoginFrom ?? undefined,
    lastLoginTo: filterCache?.lastLoginTo ?? undefined,
    name: filterCache?.name ?? undefined,
    productId: filterCache?.productId ?? undefined,
    sorts: pagReq?.sorts && pagReq?.sorts?.length > 0 ? pagReq?.sorts : undefined,
  });

  const activeTab = filterCache.status || ORGANIZATION_STATUS.ACTIVE;

  const tabs = useGetOrganizationList({
    active: data?.extra?.active ?? 0,
    archive: data?.extra?.archive ?? 0,
    deleted: data?.extra?.deleted ?? 0,
    isFetching,
  });
  const handleTabChange = (_: React.SyntheticEvent, value: string | number) => {
    // Keep the search condition when switching tabs; only reset paging.
    setFilterCache({
      ...filterCache,
      status: value as string,
      page: START_PAGE,
    });
  };

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Typography variant="head20Bold" color={text.primary}>
          組織一覧
        </Typography>
        <Typography variant="body14" color={text.secondary}>
          システムに登録されている顧客組織の一覧です。新規登録する場合は「新規顧客登録」をした後に、1人目の顧客ユーザーを登録してください。
        </Typography>
      </Stack>

      <Tab value={activeTab} selected={activeTab} options={tabs} onChange={handleTabChange}>
        {children}
      </Tab>
    </Stack>
  );
};

export default OrganizationLayout;
